import { inject, Injectable } from '@angular/core';
import { TrackingProduct } from '@libs/shop/models';
import { ProductImpressionTrackerService } from './product-impression-tracker.service';
import {
  PRODUCT_IMPRESSION_EFFECTIVE_THRESHOLD,
  PRODUCT_IMPRESSION_INTERSECTION_THRESHOLD,
  PRODUCT_IMPRESSION_VISIBILITY_DURATION,
} from './product-impression.constants';

@Injectable({
  providedIn: 'root',
})
export class ProductImpressionObserverService {
  private readonly impressionTracker: ProductImpressionTrackerService = inject(ProductImpressionTrackerService);
  private readonly elementImpressionsMap: Map<Element, TrackingProduct> = new Map<Element, TrackingProduct>();
  private readonly visibilityTimeouts: Map<Element, number> = new Map<Element, number>();
  private readonly intersectionObserver: IntersectionObserver = new IntersectionObserver(
    this.handleIntersectionEntries.bind(this),
    { threshold: [PRODUCT_IMPRESSION_EFFECTIVE_THRESHOLD, PRODUCT_IMPRESSION_INTERSECTION_THRESHOLD] },
  );

  public observeElement(element: Element, impression: TrackingProduct): void {
    this.elementImpressionsMap.set(element, impression);
    this.intersectionObserver.observe(element);
  }

  private handleIntersectionEntries(entries: IntersectionObserverEntry[]): void {
    entries.forEach((entry: IntersectionObserverEntry) => {
      window.clearTimeout(this.visibilityTimeouts.get(entry.target));
      this.visibilityTimeouts.delete(entry.target);

      // the reported ratio can be slightly below the threshold due to subpixel rounding (e.g. when zoomed)
      if (entry.intersectionRatio >= PRODUCT_IMPRESSION_EFFECTIVE_THRESHOLD) {
        this.visibilityTimeouts.set(entry.target, window.setTimeout(() => {
          this.trackImpression(entry.target);
        }, PRODUCT_IMPRESSION_VISIBILITY_DURATION));
      }
    });
  }

  private trackImpression(element: Element): void {
    const impression: TrackingProduct | undefined = this.elementImpressionsMap.get(element);

    this.intersectionObserver.unobserve(element);
    this.elementImpressionsMap.delete(element);
    this.visibilityTimeouts.delete(element);

    if (!impression) {
      return;
    }

    this.impressionTracker.track(impression);
  }
}
